import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'

/** What the sender sees when a signer declines consent without giving a reason. */
export const DECLINED_CONSENT_REASON = 'Declined electronic records consent'

interface DeclineConsentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The organization that sent the request, named so the signer knows who is told and who to contact. */
  organization: string
  declining: boolean
  error: string | null
  onDecline: (reason: string) => void
}

/**
 * Declining consent closes the request for every signer and can't be undone
 * from the signing page, so it's confirmed first, like declining to sign.
 */
export function DeclineConsentDialog({ open, onOpenChange, organization, declining, error, onDecline }: DeclineConsentDialogProps) {
  const [reason, setReason] = React.useState('')
  // A double click lands both clicks before `declining` turns the button off,
  // so send once per attempt, and re-arm when one fails or the dialog reopens.
  const sentRef = React.useRef(false)

  React.useEffect(() => {
    if (!declining) {
      sentRef.current = false
    }
  }, [declining, open])

  function decline(): void {
    if (sentRef.current) {
      return
    }
    sentRef.current = true
    onDecline(reason.trim() || DECLINED_CONSENT_REASON)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !declining && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Decline to sign electronically?</DialogTitle>
          <DialogDescription>
            This closes the request for everyone, and {organization} is told. You can’t undo it here.
            To sign another way, contact {organization}.
          </DialogDescription>
        </DialogHeader>
        <Field label="Reason (optional)" htmlFor="decline-consent-reason">
          <Textarea
            id="decline-consent-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            maxLength={2000}
          />
        </Field>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={declining} onClick={() => onOpenChange(false)}>
            Go back
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={declining}
            onClick={decline}
          >
            {declining ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Decline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
